import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";

interface EarlyAccessUser {
  _id: string;
  name: string;
  email: string;
  user_type: string;
  status: string;
  company?: string;
  role?: string;
  interest_description?: string;
  youtube_channel_id?: string;
  youtube_oauth_connected?: boolean;
  youtube_subscribers?: number;
  youtube_avg_views?: number;
  youtube_data?: any;
  imdb_url?: string;
  tiktok_handle?: string;
  instagram_handle?: string;
  project_description?: string;
  referral_code?: string;
  referred_by?: string;
  priority?: boolean;
  invited_at?: string;
  referral_source?: string;
  requested_at: string;
}

// Define all possible columns with their configurations
const ALL_COLUMNS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'user_type', label: 'Type', required: true },
  { key: 'status', label: 'Status', required: true },
  { key: 'priority', label: 'Priority' },
  { key: 'company', label: 'Company' },
  { key: 'role', label: 'Role' },
  { key: 'interest_description', label: 'Interest' },
  { key: 'youtube_channel_id', label: 'YT Channel' },
  { key: 'youtube_oauth_connected', label: 'YT OAuth' },
  { key: 'youtube_subscribers', label: 'Subscribers' },
  { key: 'youtube_avg_views', label: 'Avg Views' },
  { key: 'imdb_url', label: 'IMDB' },
  { key: 'tiktok_handle', label: 'TikTok' },
  { key: 'instagram_handle', label: 'Instagram' },
  { key: 'project_description', label: 'Project Desc' },
  { key: 'referral_source', label: 'Referral Source' },
  { key: 'referral_code', label: 'Referral Code' },
  { key: 'referred_by', label: 'Referred By' },
  { key: 'invited_at', label: 'Invited At' },
  { key: 'requested_at', label: 'Requested At', required: true },
];

// Helper function to render cell content based on column type
const renderCell = (key: string, value: any, user: EarlyAccessUser) => {
  switch (key) {
    case 'name':
      return (
        <div className="font-medium">
          {value || "N/A"}
        </div>
      );

    case 'email':
      return (
        <div className="text-muted-foreground">
          {value}
        </div>
      );

    case 'user_type':
      return (
        <Badge variant="secondary" className="capitalize bg-secondary text-secondary-foreground">
          {value || "N/A"}
        </Badge>
      );

    case 'status':
      return (
        <Badge
          variant={value === "active" ? "outline" : "secondary"}
          className={`capitalize ${value === 'active' ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-secondary text-secondary-foreground"}`}
        >
          {value || "requested"}
        </Badge>
      );

    case 'priority':
      return value ? (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
          Priority
        </Badge>
      ) : "-";

    case 'youtube_channel_id':
      return value || "-";

    case 'youtube_oauth_connected':
      return value ? (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Connected
        </Badge>
      ) : "-";

    case 'youtube_subscribers':
    case 'youtube_avg_views':
      return value ? value.toLocaleString() : "-";

    case 'imdb_url':
      return value || "-";

    case 'tiktok_handle':
    case 'instagram_handle':
      return value ? `@${value}` : "-";

    case 'interest_description':
    case 'project_description':
      return (
        <div className="max-w-[300px] truncate" title={value}>
          {value || "-"}
        </div>
      );

    case 'referral_source':
      return value ? <span className="capitalize">{value}</span> : "-";

    case 'invited_at':
    case 'requested_at':
      return value ? new Date(value).toLocaleDateString() : "-";

    default:
      return value || "-";
  }
};

export default function EarlyAccessPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL params or default to 1/10
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 10;
  const initialSearch = searchParams.get("search") || "";

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Helper to update URL params
  const updateParams = (newPage: number, newLimit: number, search: string) => {
    const params: Record<string, string> = {
      page: String(newPage),
      limit: String(newLimit)
    };
    if (search) params.search = search;
    setSearchParams(params);
  };

  // Sync search state with URL when debounced value changes
  useEffect(() => {
    if (debouncedSearch !== initialSearch) {
      updateParams(1, limit, debouncedSearch); // Reset to page 1 on search
    }
  }, [debouncedSearch]);

  // Use React Query for caching and pagination
  const { data, isLoading, isError } = useQuery({
    queryKey: ["adminEarlyAccess", page, limit, debouncedSearch],
    queryFn: () => apiClient.getAdminEarlyAccessUsers(page, limit, debouncedSearch),
    placeholderData: keepPreviousData, // Keep showing previous page data while loading new page
    staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
    refetchInterval: 10000, // Refetch every 10 seconds
  });

  const users = data ? (Array.isArray(data) ? data : data.items) : [];
  const total = data && !Array.isArray(data) ? data.total : users.length;
  const totalPages = Math.ceil(total / limit);

  // Determine which columns have data
  const visibleColumns = ALL_COLUMNS.filter(col => {
    if (col.required) return true;

    return users.some((user: EarlyAccessUser) => {
      const value = user[col.key as keyof EarlyAccessUser];
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'boolean') return value === true;
      if (typeof value === 'number') return value > 0;
      return true;
    });
  });

  if (isLoading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return <div className="text-center py-10 text-red-500">Failed to load early access users</div>;
  }

  return (
    <div className="flex flex-col w-full h-full max-w-full font-ibm overflow-hidden">
      {/* Header & Filters Container */}
      <div className="flex-none space-y-4 pb-4 max-w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <div>
              <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet">
                Early Access Users
              </h1>
              <p className="text-muted-foreground font-ibm text-sm mt-1 max-w-sm">
                Manage early access requests.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-muted-foreground h-9 px-4 flex items-center justify-center font-ibm bg-card/60 backdrop-blur-sm">
              Total: {total}
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 max-w-full">
          <div className="relative flex-1 max-w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
              <Search className="w-4 h-4" />
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 bg-card/60 backdrop-blur-sm border border-border rounded-lg text-sm placeholder:text-muted-foreground text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm font-ibm h-[38px]"
            />
          </div>
        </div>
      </div>

      {/* Scrollable Table Container */}
      <div className="flex-1 min-h-0 w-full rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="w-full h-full overflow-x-scroll overflow-y-scroll">
          <Table className="w-full">
            <TableHeader className="sticky top-0 bg-card z-10 shadow-sm border-b border-border">
              <TableRow className="hover:bg-transparent border-b border-border">
                {visibleColumns.map(col => (
                  <TableHead key={col.key} className="font-cabinet font-semibold text-foreground px-4 py-3 whitespace-nowrap">
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user: EarlyAccessUser) => (
                <TableRow key={user._id} className="hover:bg-secondary/50 border-b border-border transition-colors">
                  {visibleColumns.map(col => {
                    const value = user[col.key as keyof EarlyAccessUser];
                    return (
                      <TableCell key={col.key} className="font-ibm text-sm px-4 py-3 align-top whitespace-nowrap">
                        {renderCell(col.key, value, user)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="h-24 text-center text-muted-foreground font-ibm">
                    No early access requests found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination Controls - Fixed at Bottom */}
      <div className="flex-none pt-4 w-full max-w-full">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-muted-foreground">Rows per page</p>
            <Select
              value={`${limit}`}
              onValueChange={(value) => {
                updateParams(1, Number(value), debouncedSearch);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={limit} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex w-[100px] items-center justify-center text-sm font-medium text-muted-foreground">
              Page {page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.max(1, page - 1), limit, debouncedSearch)}
              disabled={page === 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.min(totalPages, page + 1), limit, debouncedSearch)}
              disabled={page === totalPages || totalPages === 0}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}